import { setCreateSaleStatus, createSale } from "actions/balance";
import { fetchUser } from "actions/user";
import RequireData from "hooks/RequireData";
import SageButton from "kit/SageButton";
import SageErrorAlert from "kit/SageErrorAlert";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import createSaleStatusSelector from "selectors/createSaleStatus";
import fetchUserStatusSelector from "selectors/fetchUserStatus";
import userSelector from "selectors/user";
import styles from "./BalanceMinutesSellForm.module.scss";

const BalanceMinutesSellForm: React.FC = () => {
    const dispatch = useDispatch();

    const [user] = RequireData(
        userSelector,
        fetchUserStatusSelector,
        fetchUser()
    );

    const createSaleStatus = useSelector(createSaleStatusSelector);

    const [minutes, setMinutes] = React.useState("0");

    const onMinutesChanged = React.useCallback(
        (e) => {
            const valueRaw = e.target.value.replace(/\D/g, "");
            const int = parseInt(valueRaw);
            if (isNaN(valueRaw)) {
                setMinutes("0");
            } else {
                setMinutes(int.toString());
            }
        },
        [setMinutes]
    );

    const seeAllAvailableClicked = React.useCallback(
        (e) => {
            e.preventDefault();
            if (user) {
                setMinutes(Math.floor(user.minutes_balance).toString());
            } else {
                setMinutes("0");
            }
        },
        [user, setMinutes]
    );
    const errorText = React.useMemo(() => {
        if (createSaleStatus === "error") {
            return "An unknown error occurred when creating your sale.";
        } else if (createSaleStatus === "insufficient_funds") {
            return "You don't have enough minutes to sell.";
        } else if (createSaleStatus === "invalid_input") {
            return "Invalid amount, please try again.";
        } else if (createSaleStatus === "conflict") {
            return "You have an open order. Cancel your existing orders to create a sale.";
        } else {
            return null;
        }
    }, [createSaleStatus]);

    const onErrorDismiss = React.useCallback(() => {
        dispatch(setCreateSaleStatus("none"));
    }, [dispatch]);

    const onFormSubmit = React.useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const amount = parseInt(minutes);
            dispatch(createSale(amount));
        },
        [dispatch, minutes]
    );

    return (
        <>
            <form onSubmit={onFormSubmit}>
                <div className="mb-3">
                    <label htmlFor="minutes" className="form-label">
                        Minutes to Sell
                    </label>
                    <input
                        type="number"
                        id="minutes"
                        value={minutes}
                        onChange={onMinutesChanged}
                        className="form-control"
                    />
                </div>
                <div className={styles.sellAllButton}>
                    <button
                        className="btn btn-sm btn-secondary"
                        type="button"
                        onClick={seeAllAvailableClicked}
                    >
                        Sell All Available
                    </button>
                </div>
                <div>
                    <SageButton
                        loading={createSaleStatus === "loading"}
                        text="Create Sale"
                        type="submit"
                    />
                </div>
                <p>
                    Your sale will be sold at the highest available price
                    available on the market in the order in which they are
                    recieved.
                </p>
            </form>
            {errorText && (
                <SageErrorAlert
                    text={errorText || undefined}
                    onDismiss={onErrorDismiss}
                />
            )}
        </>
    );
};

export default BalanceMinutesSellForm;
